// firebase/automations.service.js
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    increment,
    where,
    doc,
    updateDoc,
    onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase.config";
import { auth } from "./firebase.config";

/**
 * Automation status values
 */
export const AUTOMATION_STATUS = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    PAUSED: 'PAUSED',
    STOPPED: 'STOPPED',
};

/**
 * Trigger types
 */
export const TRIGGER_TYPES = {
    FORM_SUBMIT: 'FORM_SUBMIT',
    LEAD_CREATED: 'LEAD_CREATED',
    SCHEDULED: 'SCHEDULED',
};

// ============================================================
// PART 1: Frontend CRUD Operations (Client SDK)
// ============================================================

/**
 * Create a new automation draft
 * @param {Object} params - Automation parameters
 * @param {string} params.name - Automation name
 * @param {string} params.description - Automation description
 * @param {Object} params.trigger - Trigger configuration { type, config }
 * @param {Array} params.steps - Array of step definitions
 * @returns {Promise<string>} - New automation document ID
 */
export const createAutomationDraft = async ({ name, description, trigger, steps }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('User must be authenticated to create automations');
    }

    const automationsRef = collection(db, 'automations');

    const automationData = {
        ownerId: currentUser.uid,
        name: name || 'Untitled Automation',
        description: description || '',
        trigger: trigger || null,
        steps: Array.isArray(steps) ? steps : [],
        status: AUTOMATION_STATUS.DRAFT,
        version: 1,
        workflowVersion: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(automationsRef, automationData);
    return docRef.id;
};

/**
 * Update an automation draft
 * Only allows updates if status === 'DRAFT'
 * @param {string} automationId - Automation document ID
 * @param {Object} updates - Fields to update (name, description, trigger, steps)
 * @returns {Promise<void>}
 */
export const updateAutomationDraft = async (automationId, updates) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('User must be authenticated to update automations');
    }

    if (!automationId) {
        throw new Error('automationId is required');
    }

    const automationRef = doc(db, 'automations', automationId);
    const automationSnap = await getDoc(automationRef);

    if (!automationSnap.exists()) {
        throw new Error('Automation not found');
    }

    const automationData = automationSnap.data();

    // Verify ownership
    if (automationData.ownerId !== currentUser.uid) {
        throw new Error('Not authorized to update this automation');
    }

    // Only block updates for ACTIVE automations (allow DRAFT and STOPPED)
    if (automationData.status === AUTOMATION_STATUS.ACTIVE) {
        throw new Error(`Cannot update an ACTIVE automation. Stop it first.`);
    }

    // Reject updates to ownerId
    if ('ownerId' in updates) {
        delete updates.ownerId;
    }

    // Reject updates to status (use activate/deactivate functions)
    if ('status' in updates) {
        delete updates.status;
    }

    // Prepare update payload
    const updatePayload = {
        ...updates,
        updatedAt: serverTimestamp(),
    };

    // Increment workflowVersion when steps or trigger structure changes
    // This enables the execution engine to detect workflow changes and
    // migrate paused lead positions on resume
    if ('steps' in updates || 'trigger' in updates) {
        updatePayload.workflowVersion = increment(1);
    }

    await updateDoc(automationRef, updatePayload);
};

/**
 * Get a single automation by ID
 * @param {string} automationId - Automation document ID
 * @returns {Promise<Object|null>} - Automation data or null if not found
 */
export const getAutomation = async (automationId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('User must be authenticated to read automations');
    }

    if (!automationId) {
        throw new Error('automationId is required');
    }

    const automationRef = doc(db, 'automations', automationId);
    const automationSnap = await getDoc(automationRef);

    if (!automationSnap.exists()) {
        return null;
    }

    const data = automationSnap.data();

    // Verify ownership
    if (data.ownerId !== currentUser.uid) {
        throw new Error('Not authorized to read this automation');
    }

    return {
        id: automationSnap.id,
        ...data,
    };
};

/**
 * List all automations for the current user
 * @returns {Promise<Array>} - Array of automation documents
 */
export const listAutomations = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('User must be authenticated to list automations');
    }

    const automationsRef = collection(db, 'automations');
    const q = query(
        automationsRef,
        where('ownerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
    }));
};

/**
 * Subscribe to real-time updates of user's automations
 * @param {Function} callback - Called with updated automations array
 * @returns {Function} - Unsubscribe function
 */
export const onAutomationsChange = (callback) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        callback([]);
        return () => { };
    }

    const automationsRef = collection(db, 'automations');
    const q = query(
        automationsRef,
        where('ownerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const automations = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        callback(automations);
    }, (error) => {
        console.error('[automations.service] Error in automations listener:', error);
        callback([]);
    });

    return unsubscribe;
};

// ============================================================
// PART 2: Cloud Function Callables (Activation/Deactivation)
// ============================================================

/**
 * Activate an automation (Cloud Function)
 * Validates trigger and steps, then sets status to ACTIVE
 * @param {string} automationId - Automation document ID
 * @returns {Promise<Object>} - Result { success, automationId }
 */
export const activateAutomation = async (automationId) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    const callable = httpsCallable(functions, 'activateAutomation');
    const result = await callable({ automationId });
    return result.data;
};

/**
 * Deactivate an automation (Cloud Function)
 * Sets status to PAUSED
 * @param {string} automationId - Automation document ID
 * @returns {Promise<Object>} - Result { success, automationId }
 */
export const deactivateAutomation = async (automationId) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    const callable = httpsCallable(functions, 'deactivateAutomation');
    const result = await callable({ automationId });
    return result.data;
};

/**
 * Stop an automation (Cloud Function) - Hard stop
 * Sets status to STOPPED and terminates all running executions
 * @param {string} automationId - Automation document ID
 * @returns {Promise<Object>} - Result { success, automationId, executionsStopped, executionIds }
 */
export const stopAutomation = async (automationId) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    const callable = httpsCallable(functions, 'stopAutomation');
    const result = await callable({ automationId });
    return result.data;
};

/**
 * Delete an automation and all related data (Cloud Function) - Cascading delete
 * Removes automation document, all executions, tasks, and logs
 * @param {string} automationId - Automation document ID
 * @returns {Promise<Object>} - Result { success, automationId, executionsDeleted, tasksDeleted }
 */
export const deleteAutomation = async (automationId) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    const callable = httpsCallable(functions, 'deleteAutomation');
    const result = await callable({ automationId });
    return result.data;
};

/**
 * Run an automation test (Cloud Function) - Ephemeral execution
 * Executes all steps immediately using test contact data
 * Does NOT create automation_executions, does NOT affect Lead Monitor
 * @param {string} automationId - Automation document ID
 * @param {Object} testContact - Test contact { testEmail, testPhone }
 * @returns {Promise<Object>} - Result with step-by-step execution summary
 */
export const runAutomationTest = async (automationId, testContact) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    if (!testContact?.testEmail && !testContact?.testPhone) {
        throw new Error('At least testEmail or testPhone is required');
    }

    const callable = httpsCallable(functions, 'runAutomationTest');
    const result = await callable({
        automationId,
        testEmail: testContact.testEmail,
        testPhone: testContact.testPhone,
    });
    return result.data;
};

/**
 * Manually trigger an automation execution (Cloud Function)
 * Creates an execution for testing purposes
 * @param {string} automationId - Automation document ID
 * @param {Object} triggerPayload - Optional trigger data
 * @returns {Promise<Object>} - Result { success, executionId }
 */
export const startAutomationExecution = async (automationId, triggerPayload = {}) => {
    if (!automationId) {
        throw new Error('automationId is required');
    }

    const callable = httpsCallable(functions, 'startAutomationExecution');
    const result = await callable({ automationId, triggerPayload });
    return result.data;
};

// ============================================================
// PART 3: Execution History (Read-only)
// ============================================================

/**
 * Get execution history for an automation
 * @param {string} automationId - Automation document ID
 * @param {number} limit - Maximum number of executions to return
 * @returns {Promise<Array>} - Array of execution documents
 */
export const getExecutionHistory = async (automationId, limit = 20) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('User must be authenticated');
    }

    if (!automationId) {
        throw new Error('automationId is required');
    }

    const executionsRef = collection(db, 'automation_executions');
    const q = query(
        executionsRef,
        where('ownerId', '==', currentUser.uid),
        where('automationId', '==', automationId),
        orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
    }));
};

/**
 * Subscribe to real-time execution updates for an automation
 * @param {string} automationId - Automation document ID
 * @param {Function} callback - Called with updated executions array
 * @returns {Function} - Unsubscribe function
 */
export const onExecutionsChange = (automationId, callback) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !automationId) {
        callback([]);
        return () => { };
    }

    const executionsRef = collection(db, 'automation_executions');
    const q = query(
        executionsRef,
        where('ownerId', '==', currentUser.uid),
        where('automationId', '==', automationId),
        orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const executions = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        callback(executions);
    }, (error) => {
        console.error('[automations.service] Error in executions listener:', error);
        callback([]);
    });

    return unsubscribe;
};
